<template>
  <div class="card">
    <div class="card-header">
      <div class="card-title">
        Novo ajuste de horário
      </div>
    </div>
    <div class="card-body">
      <div class="row mb-3">
        <div class="col-md-5 mb-3 mb-md-0">
          <label><span class="required">*</span> Usuário</label>
          <v-select
            v-model="form.user_id"
            :options="users"
            label="name"
            :reduce="user => user.id"
            placeholder="Selecione um usuário"
            :disabled="loadingUsers || loadingPunches"
            @input="handleUserChange"
          />
        </div>
        <div class="col-md-4 mb-3 mb-md-0">
          <label><span class="required">*</span> Dia da batida</label>
          <input
            v-model="form.day"
            type="date"
            class="form-control"
            :disabled="loadingPunches"
            @change="onDayChange"
          >
        </div>
        <div class="col-md-3 d-flex align-items-end">
          <small class="text-muted">Busca automática ao selecionar usuário e dia.</small>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-md-6 mb-3 mb-md-0">
          <label><span class="required">*</span> Batida</label>
          <v-select
            v-model="form.check_id"
            :options="punchOptions"
            label="label"
            :reduce="opt => opt.value"
            placeholder="Selecione uma batida"
            :disabled="loadingPunches || punchOptions.length === 0"
            @input="handlePunchChange"
          />
          <small
            v-if="selectedPunchLabel"
            class="text-muted"
          >Horário atual: {{ selectedPunchLabel }}</small>
        </div>
        <div class="col-md-3">
          <label><span class="required">*</span> Nova data</label>
          <input
            v-model="form.new_date"
            type="date"
            class="form-control"
          >
        </div>
        <div class="col-md-3">
          <label><span class="required">*</span> Nova hora</label>
          <input
            v-model="form.new_time"
            type="time"
            class="form-control"
          >
        </div>
      </div>

      <div class="form-group">
        <label><span class="required">*</span> Justificativa</label>
        <textarea
          v-model="form.reason"
          class="form-control"
          rows="4"
        />
      </div>
      <div
        v-if="isAdmin === 1 || isAdmin === 2"
        class="form-group"
      >
        <div class="form-check">
          <input
            id="applyNow"
            v-model="form.apply_now"
            class="form-check-input"
            type="checkbox"
          >
          <label
            class="form-check-label"
            for="applyNow"
          >Aprovar e aplicar agora</label>
        </div>
      </div>
      <div class="form-group mb-0">
        <p><span class="required">*</span> Campos obrigatórios</p>
      </div>
    </div>
    <div class="card-footer d-flex justify-content-end">
      <router-link
        to="/check-adjustment-requests"
        class="btn btn-secondary"
      >
        Voltar
      </router-link>
      <button
        class="btn btn-primary ml-2"
        :disabled="saving"
        @click="save"
      >
        {{ saving ? 'Salvando...' : 'Salvar' }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

export default {
  data() {
    return {
      form: {
        user_id: null,
        check_id: null,
        day: moment().format('YYYY-MM-DD'),
        new_date: moment().format('YYYY-MM-DD'),
        new_time: moment().format('HH:mm'),
        reason: '',
        apply_now: true,
      },
      punchOptions: [],
      loadingPunches: false,
      users: [],
      loadingUsers: false,
      saving: false,
      _fetchTimer: null,
    }
  },
  computed: {
    isAdmin() {
      const v = localStorage.getItem('is_admin')
      return parseInt(v, 10) || 0
    },
    selectedPunch() {
      return this.punchOptions.find(p => p.value === this.form.check_id) || null
    },
    selectedPunchLabel() {
      return this.selectedPunch ? this.selectedPunch.label : ''
    },
  },
  created() {
    this.fetchUsers()
    if (this.isAdmin !== 1 && this.isAdmin !== 2) {
      this.form.apply_now = false
    }
    // auto-load punches when opening if user/day already filled
    this.scheduleFetchPunches()
  },
  methods: {
    async fetchUsers() {
      this.loadingUsers = true
      const api = `${this.$store.state.api}users/active`
      try {
        const res = await axios.get(api)
        this.users = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        this.$message('Erro', 'Não foi possível carregar usuários', 'error')
      } finally {
        this.loadingUsers = false
      }
    },
    setNewDateTimeFromPunch(punch) {
      const m = moment(punch && punch.created_at)
      if (m.isValid()) {
        this.form.new_date = m.format('YYYY-MM-DD')
        this.form.new_time = m.format('HH:mm')
      }
    },
    async fetchPunches(silent = true) {
      if (!this.form.user_id || !this.form.day) {
        if (!silent) this.$message('Erro', 'Selecione usuário e dia para buscar batidas', 'error')
        return
      }
      const dayMoment = moment(this.form.day, 'YYYY-MM-DD')
      if (!dayMoment.isValid()) {
        if (!silent) this.$message('Erro', 'Data inválida', 'error')
        return
      }

      this.loadingPunches = true
      const year = dayMoment.year()
      const month = dayMoment.month() // 0-11 conforme API existente
      const dayParam = dayMoment.format('YYYY-MM-DD')
      const api = `${this.$store.state.api}checks/myChecks?year=${year}&month=${month}&user_id=${this.form.user_id}&day=${dayParam}`

      try {
        const res = await axios.get(api)
        const payload = res.data?.data || res.data || {}
        const timeline = payload.organizedChecksTimeline || {}
        const dayTimeline = timeline[dayParam] || []

        const punchesFromTimeline = (dayTimeline || [])
          .filter(item => item && item.type === 'punch')
          .map(item => {
            const punch = item.punch || {
              id: item.id,
              created_at: item.created_at,
              ...item.meta,
            }
            return {
              value: punch.id,
              label: moment(punch.created_at).format('DD/MM/YYYY HH:mm'),
              punch,
            }
          })

        // fallback: organizedChecks (caso timeline venha vazio)
        const dayChecksObj = (payload.organizedChecks && payload.organizedChecks[dayParam]) || {}
        const punchesFromChecks = Object.values(dayChecksObj)
          .filter(v => v && typeof v === 'object' && v.created_at)
          .map(p => ({
            value: p.id,
            label: moment(p.created_at).format('DD/MM/YYYY HH:mm'),
            punch: p,
          }))

        const punches = punchesFromTimeline.length ? punchesFromTimeline : punchesFromChecks

        this.punchOptions = punches
        if (!punches.length) {
          this.form.check_id = null
          this.$message('Atenção', 'Nenhuma batida encontrada para o dia/usuário', 'warning')
          return
        }

        const alreadySelected = punches.find(p => p.value === this.form.check_id)
        if (!alreadySelected) {
          const first = punches[0]
          this.form.check_id = first.value
          this.setNewDateTimeFromPunch(first.punch)
        } else {
          this.setNewDateTimeFromPunch(alreadySelected.punch)
        }
      } catch (e) {
        const msg = e.response && e.response.data ? e.response.data : 'Erro ao buscar batidas'
        this.$message('Erro', msg, 'error')
      } finally {
        this.loadingPunches = false
      }
    },
    handleUserChange() {
      this.form.check_id = null
      this.punchOptions = []
      this.scheduleFetchPunches()
    },
    handlePunchChange() {
      const selected = this.selectedPunch
      if (selected && selected.punch) {
        this.setNewDateTimeFromPunch(selected.punch)
      }
    },
    scheduleFetchPunches() {
      if (this._fetchTimer) clearTimeout(this._fetchTimer)
      this._fetchTimer = setTimeout(() => {
        this.fetchPunches(true)
      }, 250)
    },
    onDayChange() {
      this.form.check_id = null
      this.punchOptions = []
      this.scheduleFetchPunches()
    },
    async save() {
      if (!this.form.user_id || !this.form.check_id || !this.form.new_date || !this.form.new_time || !this.form.reason) {
        this.$message('Erro', 'Preencha todos os campos obrigatórios', 'error')
        return
      }

      const requestedDateTime = `${this.form.new_date} ${this.form.new_time}:00`
      const api = `${this.$store.state.api}check-adjustment-requests`
      const payload = {
        check_id: this.form.check_id,
        requested_created_at: requestedDateTime,
        reason: this.form.reason,
      }
      if (this.isAdmin === 1 || this.isAdmin === 2) {
        payload.apply_now = !!this.form.apply_now
      }

      this.saving = true
      try {
        await axios.post(api, payload)
        this.$message('Sucesso', 'Ajuste criado', 'success')
        this.$router.push('/check-adjustment-requests')
      } catch (e) {
        this.$message('Erro', 'Não foi possível salvar', 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.required {
  color: #dc3545;
  font-weight: bold;
}
</style>
